import * as FuzzyMatch from '@wandb/weave/common/util/fuzzyMatch';
import {TimeDelta} from '@wandb/weave/common/util/time';
import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import React, {useMemo} from 'react';
import {NavLink} from 'react-router-dom';
import {CellInfo, Column} from 'react-table';
import TimeAgo from 'react-timeago';

import {useAllTeamProjectsQuery} from '../generated/graphql';
import {InstrumentedLoader as Loader} from './../components/utility/InstrumentedLoader';
import * as S from './ProjectsTableUser.styles';
import WBReactTable from './WBReactTable';

export type ProjectsTableProps = {
  className?: string;
  entityName: string;
  pageSize?: number | null;
  isTeam?: boolean;
  onChangePageSize?: (pageSize: number) => void;
};

const ProjectsTable: React.FC<ProjectsTableProps> = ({
  className,
  entityName,
  pageSize,
  onChangePageSize,
}) => {
  // TODO(gst): page this table
  const allTeamProjectsQuery = useAllTeamProjectsQuery({
    variables: {entityName},
  });

  const projects = useMemo(
    () =>
      allTeamProjectsQuery.data?.projects?.edges
        .map(e => e.node)
        .sort((a, b) => {
          // order by the most recent items, see note on query
          if (!a?.lastActive || !b?.lastActive) {
            return 0;
          }
          const aDate = new Date(a.lastActive);
          const bDate = new Date(b.lastActive);
          return bDate.getTime() - aDate.getTime();
        }) ?? [],
    [allTeamProjectsQuery.data]
  );

  const rows = useMemo(
    () =>
      projects.filter(isNotNullOrUndefined).map(p => ({
        searchString: p.name,
        row: {
          name: p.name,
          lastRun: p.lastActive,
          runCount: p.totalRuns,
          edit: p,
          compute: p.computeHours,
        },
      })),
    [projects]
  );

  const columns = React.useMemo(() => {
    const nameColumn: Column = {
      Header: 'Name',
      accessor: 'name',
      Cell: (cellInfo: CellInfo) => {
        const projectHome: string = entityName;
        const projectName: string = cellInfo.value;
        return (
          <div>
            <NavLink to={`/${projectHome}/${projectName}`}>
              {FuzzyMatch.fuzzyMatchHighlightPieces([projectName])}
            </NavLink>
          </div>
        );
      },
    };
    const lastRunColumn: Column = {
      Header: 'Last run',
      accessor: 'lastRun',
      Cell: (cellInfo: CellInfo) => {
        if (cellInfo.value == null) {
          return <span>-</span>;
        }
        const runDate = new Date(cellInfo.value);

        const dayOld = Date.now() - 24 * 60 * 60 * 1000;
        if (runDate.getTime() > dayOld) {
          return <TimeAgo date={runDate} />;
        }

        const runDateString = runDate.toISOString().substring(0, 10);
        return <span>{runDateString}</span>;
      },
    };
    const runCountColumn: Column = {
      Header: 'Runs',
      accessor: 'runCount',
      Cell: (cellInfo: CellInfo) => {
        return <span>{cellInfo.value}</span>;
      },
    };
    const computeHourColumn: Column = {
      Header: 'Cumulative tracked hours',
      accessor: 'compute',
      Cell: (cellInfo: CellInfo) => {
        return (
          <span>{new TimeDelta(cellInfo.value).toSingleUnitString()}</span>
        );
      },
    };
    const allColumns = [
      nameColumn,
      lastRunColumn,
      runCountColumn,
      computeHourColumn,
    ];
    return allColumns;
  }, [entityName]);

  if (allTeamProjectsQuery.loading) {
    return (
      <S.LoaderWrapper>
        <Loader name="projects-table-compute-hour-loader" />
      </S.LoaderWrapper>
    );
  }

  return (
    <S.Wrapper className={className}>
      <S.HeaderWrapper></S.HeaderWrapper>
      <WBReactTable
        data={rows}
        columns={columns}
        pageSize={pageSize ?? 20}
        onChangePageSize={onChangePageSize}
      />
    </S.Wrapper>
  );
};

export default ProjectsTable;
